import type { ReactNode } from "react";

import { Button, Flex, Typography } from "antd";
import { Trans, useTranslation } from "react-i18next";

import { useSandboxLogo } from "sandbox/hooks";

import s from "./TermsOfUseScreen.module.css";

const Section = ({
  heading,
  children,
}: {
  children: ReactNode;
  heading: string;
}) => (
  <Flex
    vertical
    align="flex-start"
    gap="middle"
    component="section"
    className={s.section}
  >
    <Typography.Title level={4} className={s.sectionHeading}>
      {heading}
    </Typography.Title>
    {children}
  </Flex>
);

export const TermsOfUseScreen = () => {
  const { t } = useTranslation("sandbox");
  const logo = useSandboxLogo();

  const base = "components.termsOfUse.sections";

  return (
    <Flex vertical component="section" className={s.page}>
      <Flex align="center" gap="small" component="header" className={s.header}>
        <img src={logo} alt="Thredd" className={s.logo} />
        <span className={s.brandPill}>{t("components.header.brandPill")}</span>
      </Flex>

      <Flex vertical align="center" className={s.body}>
        <div className={s.card}>
          <Typography.Title level={2} className={s.title}>
            {t("components.termsOfUse.title")}
          </Typography.Title>
          <Typography.Title level={5} className={s.intro}>
            {t("components.termsOfUse.intro")}
          </Typography.Title>
          <Section heading={t(`${base}.whoWeAre.heading`)}>
            <p>
              <a
                href={t(`${base}.whoWeAre.siteUrl`)}
                target="_blank"
                rel="noreferrer"
                className={s.link}
              >
                {t(`${base}.whoWeAre.siteUrl`)}
              </a>{" "}
              {t(`${base}.whoWeAre.body`)}
            </p>
          </Section>

          <Section heading={t(`${base}.otherTerms.heading`)}>
            <p>{t(`${base}.otherTerms.intro`)}</p>
            <ul>
              <li>
                <Trans
                  i18nKey={`${base}.otherTerms.items.privacyPolicy`}
                  t={t}
                  components={{
                    privacyPolicyLink: (
                      <Button type="link" className={s.linkButton} />
                    ),
                  }}
                />
              </li>
              <li>{t(`${base}.otherTerms.items.termsOfUsePolicy`)}</li>
              <li>
                <Trans
                  i18nKey={`${base}.otherTerms.items.cookiePolicy`}
                  t={t}
                  components={{
                    cookiePolicyLink: (
                      <Button type="link" className={s.linkButton} />
                    ),
                  }}
                />
              </li>
              <li>
                <Trans
                  i18nKey={`${base}.otherTerms.items.websiteTerms`}
                  t={t}
                  components={{
                    websiteTermsLink: (
                      <Button type="link" className={s.linkButton} />
                    ),
                  }}
                />
              </li>
            </ul>
          </Section>

          <Section heading={t(`${base}.changesToTerms.heading`)}>
            <p>{t(`${base}.changesToTerms.body`)}</p>
          </Section>

          <Section heading={t(`${base}.materialUse.heading`)}>
            <p>{t(`${base}.materialUse.paragraphs.intellectualProperty`)}</p>
            <p>{t(`${base}.materialUse.paragraphs.personalUseCopy`)}</p>
            <p>{t(`${base}.materialUse.paragraphs.attributionRequired`)}</p>
          </Section>

          <Section heading={t(`${base}.prohibitedUses.heading`)}>
            <p>{t(`${base}.prohibitedUses.intro`)}</p>
            <ul>
              <li>
                {t(`${base}.prohibitedUses.items.unlawfulRegulationBreach`)}
              </li>
              <li>{t(`${base}.prohibitedUses.items.fraudulentPurpose`)}</li>
              <li>{t(`${base}.prohibitedUses.items.malwareTransmission`)}</li>
            </ul>
            <p>{t(`${base}.prohibitedUses.agreeIntro`)}</p>
            <ul>
              <li>{t(`${base}.prohibitedUses.agreeItems.noReproduction`)}</li>
              <li>
                {t(
                  `${base}.prohibitedUses.agreeItems.noCommercialUseWithoutLicence`,
                )}
              </li>
              <li>
                {t(
                  `${base}.prohibitedUses.agreeItems.noUnauthorisedAccessIntro`,
                )}
                <ul>
                  <li>{t(`${base}.prohibitedUses.accessItems.site`)}</li>
                  <li>
                    {t(`${base}.prohibitedUses.accessItems.hostingEquipment`)}
                  </li>
                  <li>
                    {t(`${base}.prohibitedUses.accessItems.hostingSoftware`)}
                  </li>
                  <li>
                    {t(
                      `${base}.prohibitedUses.accessItems.thirdPartyEquipment`,
                    )}
                  </li>
                </ul>
              </li>
            </ul>
          </Section>

          <Section heading={t(`${base}.contentStandards.heading`)}>
            <p>{t(`${base}.contentStandards.intro`)}</p>
            <p>{t(`${base}.contentStandards.paragraphs.spiritAndLetter`)}</p>
            <p>{t(`${base}.contentStandards.paragraphs.threddDiscretion`)}</p>
            <p>{t(`${base}.contentStandards.mustIntro`)}</p>
            <ul>
              <li>{t(`${base}.contentStandards.mustItems.artificialData`)}</li>
              <li>{t(`${base}.contentStandards.mustItems.lawfulUseOnly`)}</li>
            </ul>
            <p>{t(`${base}.contentStandards.mustNotIntro`)}</p>
            <ul>
              <li>
                {t(`${base}.contentStandards.mustNotItems.noPersonalData`)}
              </li>
              <li>
                {t(
                  `${base}.contentStandards.mustNotItems.noDatabaseRightInfringement`,
                )}
              </li>
              <li>
                {t(
                  `${base}.contentStandards.mustNotItems.noThirdPartyDutyBreach`,
                )}
              </li>
              <li>
                {t(`${base}.contentStandards.mustNotItems.noImpersonation`)}
              </li>
              <li>
                {t(
                  `${base}.contentStandards.mustNotItems.noFalseThreddAssociation`,
                )}
              </li>
              <li>
                {t(`${base}.contentStandards.mustNotItems.noMaliciousCode`)}
              </li>
            </ul>
          </Section>

          <Section heading={t(`${base}.breachOfPolicy.heading`)}>
            <p>{t(`${base}.breachOfPolicy.paragraphs.breachDetermination`)}</p>
            <p>
              {t(`${base}.breachOfPolicy.paragraphs.immediateRightCessation`)}
            </p>
            <p>{t(`${base}.breachOfPolicy.actionsIntro`)}</p>
            <ul>
              <li>
                {t(`${base}.breachOfPolicy.actionsItems.withdrawalOfAccess`)}
              </li>
              <li>
                {t(`${base}.breachOfPolicy.actionsItems.removalOfContribution`)}
              </li>
              <li>{t(`${base}.breachOfPolicy.actionsItems.warning`)}</li>
              <li>
                {t(`${base}.breachOfPolicy.actionsItems.legalProceedings`)}
              </li>
              <li>
                {t(`${base}.breachOfPolicy.actionsItems.furtherLegalAction`)}
              </li>
              <li>
                {t(
                  `${base}.breachOfPolicy.actionsItems.disclosureToAuthorities`,
                )}
              </li>
            </ul>
            <p>{t(`${base}.breachOfPolicy.liabilityExclusion`)}</p>
          </Section>

          <Section heading={t(`${base}.suspendSite.heading`)}>
            <p>{t(`${base}.suspendSite.paragraphs.freeOfCharge`)}</p>
            <p>{t(`${base}.suspendSite.paragraphs.userResponsibility`)}</p>
          </Section>

          <Section heading={t(`${base}.accountSafety.heading`)}>
            <p>
              {t(`${base}.accountSafety.paragraphs.confidentialCredentials`)}
            </p>
            <p>{t(`${base}.accountSafety.paragraphs.disableCredentials`)}</p>
            <p>
              <Trans
                i18nKey={`${base}.accountSafety.paragraphs.notifyBreach`}
                t={t}
                components={{
                  infosecLink: (
                    // oxlint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
                    <a href="mailto:infosec@thredd.com" className={s.link} />
                  ),
                  sandboxLink: (
                    // oxlint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
                    <a href="mailto:sandbox@thredd.com" className={s.link} />
                  ),
                }}
              />
            </p>
          </Section>

          <Section heading={t(`${base}.noReliance.heading`)}>
            <p>{t(`${base}.noReliance.paragraphs.generalInformationOnly`)}</p>
            <p>{t(`${base}.noReliance.paragraphs.noAccuracyGuarantee`)}</p>
          </Section>

          <Section heading={t(`${base}.liability.heading`)}>
            <p>{t(`${base}.liability.intro`)}</p>
            <ul>
              <li>{t(`${base}.liability.items.domesticUseOnly`)}</li>
              <li>
                {t(
                  `${base}.liability.items.noLiabilityExclusionForDeathOrInjury`,
                )}
              </li>
              <li>{t(`${base}.liability.items.noImpliedWarranties`)}</li>
              <li>{t(`${base}.liability.items.noLiabilityForLossOrDamage`)}</li>
              <li>
                {t(`${base}.liability.items.noLiabilityForConsequentialLoss`)}
              </li>
            </ul>
          </Section>

          <Section heading={t(`${base}.personalInformation.heading`)}>
            <p>
              <Trans
                i18nKey={`${base}.personalInformation.body`}
                t={t}
                components={{
                  termsLink: <Button type="link" className={s.linkButton} />,
                }}
              />
            </p>
          </Section>

          <Section heading={t(`${base}.uploadedContent.heading`)}>
            <p>{t(`${base}.uploadedContent.intro`)}</p>
            <ul>
              <li>{t(`${base}.uploadedContent.items.contentLicenceGrant`)}</li>
            </ul>
          </Section>

          <Section heading={t(`${base}.viruses.heading`)}>
            <p>{t(`${base}.viruses.paragraphs.noSecurityGuarantee`)}</p>
            <p>{t(`${base}.viruses.paragraphs.userDeviceResponsibility`)}</p>
            <p>{t(`${base}.viruses.paragraphs.noMaliciousAttacks`)}</p>
          </Section>

          <Section heading={t(`${base}.governingLaw.heading`)}>
            <p>{t(`${base}.governingLaw.body`)}</p>
          </Section>
        </div>
      </Flex>
    </Flex>
  );
};
